import React, { useState, useEffect, useRef } from 'react';
import { Send, Paperclip, MoreVertical, Reply, Flag, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import ReportMessageModal from '../ReportMessageModal';

const MessageThread = ({ conversationId, currentUserId, conversation }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [messageToReport, setMessageToReport] = useState(null);
  const [myIpAddress, setMyIpAddress] = useState(null);
  const messagesEndRef = useRef(null);
  const previousMessageIdsRef = useRef(new Set());
  
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  
  // Check if trainer account is deleted
  const isTrainerDeleted = conversation?.trainer_deleted || false;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (conversationId) {
      fetchMessages();
      fetchMyIP();
      previousMessageIdsRef.current = new Set();
      
      // Scroll to bottom on initial load only
      const scrollTimer = setTimeout(() => scrollToBottom(), 500);
      
      const interval = setInterval(fetchMessages, 2000);
      return () => {
        clearInterval(interval);
        clearTimeout(scrollTimer);
      };
    }
  }, [conversationId]);

  // Auto-scroll only when NEW messages arrive (not on every fetch)
  useEffect(() => {
    if (messages.length > 0 && messagesEndRef.current) {
      const currentMessageIds = new Set(messages.map(m => m.id));
      const previousIds = previousMessageIdsRef.current;
      
      // Check if there are new messages
      const hasNewMessages = messages.some(m => !previousIds.has(m.id));
      
      if (hasNewMessages && previousIds.size > 0) {
        // New message arrived - scroll to bottom
        scrollToBottom();
      }
      
      // Update previous IDs
      previousMessageIdsRef.current = currentMessageIds;
    }
  }, [messages]);

  const fetchMyIP = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/users/my-ip`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMyIpAddress(data.ip_address);
      }
    } catch (error) {
      console.error('Error fetching IP:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE}/api/messages/conversation/${conversationId}?limit=100`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        const msgs = data.messages || [];
        
        // Only update if message count changed (avoid unnecessary re-renders and scroll)
        if (msgs.length !== messages.length) {
          setMessages(msgs);
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || newMessage.length > 5000) {
      toast({ 
        title: 'Error', 
        description: 'Message must be 1-5000 characters', 
        variant: 'destructive' 
      });
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/messages/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          content: newMessage,
          replied_to_id: replyingTo?.id
        })
      });

      if (response.ok) {
        setNewMessage('');
        setReplyingTo(null);
        await fetchMessages();
        // Scroll after sending message
        setTimeout(() => scrollToBottom(), 300);
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to send message', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Report success handler - refresh messages if needed
  const handleReportSuccess = () => {
    toast({
      title: '✅ Report Submitted',
      description: 'Message reported to admin successfully'
    });
    // Optionally refresh messages
    fetchMessages();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Security Notice & IP Display */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        {/* Security Warning Banner */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 px-4 py-3 border-b border-amber-200 dark:border-amber-700">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-300">
                Security Notice
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-400 mt-1">
                All conversations are monitored and recorded for your safety. Please maintain respectful communication at all times.
              </p>
            </div>
          </div>
        </div>

        {/* Modern IP Display */}
        {myIpAddress && (
          <div className="bg-slate-50 dark:bg-slate-900/50 px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Your IP Address</p>
                    <p className="text-sm font-mono font-semibold text-gray-900 dark:text-gray-100">{myIpAddress}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Tracked for security</span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Messages List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white dark:bg-gray-900">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender_id === currentUserId ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[70%] rounded-lg p-3 ${
                message.sender_id === currentUserId
                  ? 'bg-gradient-to-br from-red-600 to-red-800 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
              }`}
            >
              {message.sender_id !== currentUserId && (
                <p className="text-xs font-semibold mb-1">{message.sender_name}</p>
              )}
              
              {message.replied_to_id && (
                <div className="text-xs opacity-75 mb-2 pl-2 border-l-2 border-current">
                  Replying to previous message
                </div>
              )}
              
              <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
              
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs opacity-75">
                  {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {message.is_edited && ' (edited)'}
                </span>
                
                {/* Report icon - only for messages from other users */}
                {message.sender_id !== currentUserId && (
                  <button
                    onClick={() => {
                      setMessageToReport(message);
                      setReportModalOpen(true);
                    }}
                    className="ml-2 opacity-75 hover:opacity-100 transition-opacity"
                    title="Report this message"
                  >
                    <AlertTriangle className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
        {/* Deleted Trainer Warning */}
        {isTrainerDeleted && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-900 dark:text-red-300">Account Deleted</p>
                <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                  This trainer has deleted their account. You can no longer send messages, but you can still view your conversation history.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {!isTrainerDeleted && replyingTo && (
          <div className="mb-2 p-2 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
              <Reply className="w-4 h-4" />
              <span className="text-sm">Replying to message</span>
            </div>
            <button onClick={() => setReplyingTo(null)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              ×
            </button>
          </div>
        )}
        
        <form onSubmit={sendMessage} className="flex gap-2">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={isTrainerDeleted ? "This trainer has deleted their account - cannot send messages" : "Type a message..."}
            className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed focus:ring-2 focus:ring-red-500 focus:border-transparent"
            rows={2}
            maxLength={5000}
            disabled={loading || isTrainerDeleted}
          />
          <Button type="submit" disabled={loading || !newMessage.trim() || isTrainerDeleted}>
            <Send className="w-4 h-4" />
          </Button>
        </form>
        {!isTrainerDeleted && (
          <p className="text-xs text-gray-500 mt-1">
            {newMessage.length}/5000 characters
          </p>
        )}
      </div>

      {/* Report Message Modal */}
      <ReportMessageModal
        isOpen={reportModalOpen}
        onClose={() => {
          setReportModalOpen(false);
          setMessageToReport(null);
        }}
        message={messageToReport}
        onReportSuccess={handleReportSuccess}
      />
    </div>
  );
};

export default MessageThread;